import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { fetchHistoricalSensorData, parseAmbientData } from "../../utils/mqttUtils";
import { getSensorType, getActuatorLow, getActuatorHigh, calculateLightThresholds, convertThresholdsToTimes, normalizeToTimeInputFormat, supportsImmediateTrigger, handleImmediateActionSafeguard, isIrrigationActuator, formatActionFailureMessage } from "../../utils/actionUtils";
import { useCardData } from "../common/CardContext";
import { useCards, useActions, useUpdateCardMutation } from "../../utils/reactQueryHooks";
import { mqttTimeRangeOptions, ACTION_CONFIGS } from "../../utils/constants";
import { pauseActuatorProbes, publishMQTTStatus } from "../../utils/mqttUtils";
import { useUpdateActionMutation } from '../../utils/reactQueryHooks';
import Loading from "../common/Loading";
import MqttChart from "../mqtt/MqttChart";
import OverlayChart from "../mqtt/OverlayChart";
import ChatBot from "../common/ChatBot";
import AutomationTab from "./AutomationTab";
import SensorStatusAnimation from "./SensorStatusAnimation";
import WateringStatus from "./WateringStatus";
import { useActuatorPresence } from "./ActuatorPresence";
import { formatAge } from "../../utils/timeUtils";
import ActionErrorModal from "./ActionErrorModal";
import InlineRename from "../common/InlineRename";
import { useChatUIPersistence } from "../common/ChatUIPersistenceContext";
import chatIcon from "../../assets/images/chat.png";
import "../../styles/chatbot.css";
import "../../styles/automation.css";
import "../../styles/sensorStatusAnimation.css";
import "../../styles/actionErrorModal.css";


export default function ActionWrapper() {
    const { macAddress } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Debug: Track macAddress from URL params
    useEffect(() => {
        console.log('🔍 URL macAddress:', macAddress);
    }, [macAddress]);
    
    // Handle back navigation
    const handleBack = () => {
        navigate(-1);
    };
    
    // Chatbot state
    const { isChatModalOpen, setIsChatModalOpen } = useChatUIPersistence();
    
    // Tab state
    const [activeTab, setActiveTab] = useState('controls');
    
    // Chart overlay state
    const [isChartOverlay, setIsChartOverlay] = useState(false);
    
    // Tooltip state for checkbox info icons
    const [showTooltip, setShowTooltip] = useState(null);
    
    // ChatBot functions
    const openChatBot = () => setIsChatModalOpen(prev => !prev);
    const closeChatBot = () => setIsChatModalOpen(false);

    const locationState = location.state || {};
    const locationAction = locationState.action || {};


    // Setup React Query hooks
    const updateOneCardMutation = useUpdateCardMutation();
    const updateOneActionMutation = useUpdateActionMutation();
    
    // Use React Query to fetch/refresh actions data
    const { data: actions = [], isLoading: isActionsLoading } = useActions();
    const actionCard = useMemo(() => 
        actions.find(action => action.macAddress === macAddress),
    [actions, macAddress]
    );

    // Use React Query to fetch cards instead of legacy getCard
    const { data: cards = [] } = useCards();
    

    const sensorCard = useMemo(() => 
        actionCard ? cards.find(card => card.macAddress === actionCard.sensor) || null : null,
    [cards, actionCard]);
    

    
    // Derive actuator type safely
    const actType = useMemo(() => actionCard?.actuatorType || '', [actionCard?.actuatorType]);
    const senseType = useMemo(() => getSensorType(actionCard?.actuatorType || ''), [actionCard?.actuatorType]);
    // Derive action config
    const actionConfig = useMemo(() => {
        if (!actType) return { settings: [] };
        return ACTION_CONFIGS[actType] || { settings: [] };
    }, [actType]);

    // Initialize all state hooks unconditionally
    const [thresholds, setThresholds] = useState({ low: null, high: null });

    // Map sensor type to correct threshold values from sensor card
    const getSensorThresholds = (sensorType, sensorCard) => {
        if (!sensorCard) return { low: null, high: null };
        
        switch (sensorType?.toLowerCase()) {
            case 'distance':
                return {
                    low: sensorCard.waterLow ? parseFloat(sensorCard.waterLow) : null,
                    high: sensorCard.waterHigh ? parseFloat(sensorCard.waterHigh) : null
                };
            case 'humidity':
                return {
                    low: sensorCard.humidityLow ? parseFloat(sensorCard.humidityLow) : null,
                    high: sensorCard.humidityHigh ? parseFloat(sensorCard.humidityHigh) : null
                };
            case 'temperature':
                return {
                    low: sensorCard.temperatureLow ? parseFloat(sensorCard.temperatureLow) : null,
                    high: sensorCard.temperatureHigh ? parseFloat(sensorCard.temperatureHigh) : null
                };
            case 'light':
            case 'lux':
                return {
                    low: sensorCard.lightLow ? parseFloat(sensorCard.lightLow) : null,
                    high: sensorCard.lightHigh ? parseFloat(sensorCard.lightHigh) : null
                };
            default:
                return { low: null, high: null };
        }
    };

    // Handle sending messages to the chatbot
    const [selectedTimeRange, setSelectedTimeRange] = useState("1d");
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [mqttStatus, setMqttStatus] = useState(null); // success / in-progress banner only (not errors)
    const [actionError, setActionError] = useState(null); // { title, message } — blocking modal, errors only
    const [animationStatus, setAnimationStatus] = useState('idle'); // 'idle', 'connecting', 'sending', 'verifying', 'success', 'error'
    const [historicalSensorData, setHistoricalSensorData] = useState([]);
    const [historicalActionData, setHistoricalActionData] = useState([]);
    
    const { cardData } = useCardData();
    
    // Initialize action settings
    const [actionSettings, setActionSettings] = useState(() => {
        const initialSettings = {};
        // Safely initialize the settings without relying on conditional variables
        if (actType && ACTION_CONFIGS[actType] && actionCard) {
            ACTION_CONFIGS[actType].settings.forEach(setting => {
                if (actType === 'light') {
                    // For light actions, initialize time selectors and timezone
                    if (setting.key === 'startTime' || setting.key === 'endTime') {
                        // For light actions, convert stored thresholds to times using the same logic as Action.jsx
                        if (sensorCard) {
                            const lightLow = getActuatorLow(actType, sensorCard);
                            const lightHigh = getActuatorHigh(actType, sensorCard);
                            const { startTime, endTime } = convertThresholdsToTimes(lightLow, lightHigh, actionCard?.timezone || 'America/Chicago');
                            
                            if (setting.key === 'startTime') {
                                initialSettings.startTime = startTime;
                            } else if (setting.key === 'endTime') {
                                initialSettings.endTime = endTime;
                            }
                        } else {
                            // Fallback to action card values if sensor card not available
                            if (setting.key === 'startTime') {
                                initialSettings.startTime = actionCard?.startTime || '08:00 AM';
                            } else if (setting.key === 'endTime') {
                                initialSettings.endTime = actionCard?.endTime || '08:00 PM';
                            }
                        }
                    } else if (setting.key === 'timezone') {
                        initialSettings.timezone = actionCard?.timezone ?? 'America/Chicago';
                    }
                } else if (setting.key === 'tracking') {
                    // For tracking dropdown, use actionCard.tracking or default to '1h' (1 hour)
                    initialSettings.tracking = actionCard?.tracking ?? '1h';
                } else if (setting.key === 'duration') {
                    // For irrigation duration, use actionCard.duration or default to 4 seconds
                    initialSettings.duration = actionCard?.duration ?? 4;
                } else if (setting.key === 'sensorTracking') {
                    // For sensor tracking checkbox, use actionCard.sensorTracking or default to true
                    if (actionCard?.sensorTracking === undefined) {
                        console.warn('[SENSOR_TRACKING] ⚠️ BACKEND ISSUE: sensorTracking field is undefined!', {
                            macAddress: actionCard?.macAddress,
                            actionCard,
                            message: 'Backend GET /actions endpoint is NOT returning sensor_tracking field. Using default: true',
                            fix: 'Backend engineer: Add sensor_tracking to SELECT query and response mapping'
                        });
                    }
                    initialSettings.sensorTracking = actionCard?.sensorTracking ?? true;
                } else {
                    // For other non-light action settings, use the existing behavior
                    initialSettings[setting.key] = actionCard?.[setting.key] ?? 
                    (setting.type === "select" ? setting.options[0].value : "");
                }
            });
        }
        return initialSettings;
    });

    // Update light action settings when sensor card or action card changes
    useEffect(() => {
        if (actType !== 'light' || !actionCard) return;
        
        // For light actions, convert stored thresholds to times using the same logic as Action.jsx
        if (sensorCard) {
            const lightLow = getActuatorLow(actType, sensorCard);
            const lightHigh = getActuatorHigh(actType, sensorCard);
            const { startTime, endTime } = convertThresholdsToTimes(lightLow, lightHigh, actionCard?.timezone || 'America/Chicago');
            
            setActionSettings(prevSettings => ({
                ...prevSettings,
                startTime: startTime,
                endTime: endTime,
                timezone: actionCard?.timezone || prevSettings.timezone || 'America/Chicago'
            }));
        } else {
            // Fallback to action card values if sensor card not available
            setActionSettings(prevSettings => ({
                ...prevSettings,
                startTime: actionCard?.startTime || prevSettings.startTime || '08:00 AM',
                endTime: actionCard?.endTime || prevSettings.endTime || '08:00 PM',
                timezone: actionCard?.timezone || prevSettings.timezone || 'America/Chicago'
            }));
        }
    }, [
      actType,
      actionCard,
      sensorCard,
      sensorCard?.lightLow,
      sensorCard?.lightHigh,
      actionCard?.timezone,
    ]);

     // Update thresholds when sensor card is available
     useEffect(() => {
        if (!sensorCard) return;
        const newLow = parseFloat(getActuatorLow(actType, sensorCard));
        const newHigh = parseFloat(getActuatorHigh(actType, sensorCard));
        setThresholds({
            low: newLow,
            high: newHigh
        });
    }, [sensorCard, actType, sensorCard?.humidityLow, sensorCard?.humidityHigh, sensorCard?.temperatureLow, sensorCard?.temperatureHigh, sensorCard?.waterLow, sensorCard?.waterHigh, sensorCard?.lightLow, sensorCard?.lightHigh]);

    // Memoize the sensor and action MAC addresses to prevent unnecessary re-fetches
    const sensorMacAddress = useMemo(() => {
        console.log('🔍 sensorMacAddress memoized:', actionCard?.sensor);
        return actionCard?.sensor;
    }, [actionCard?.sensor]);
    const actionMacAddress = useMemo(() => {
        console.log('🔍 actionMacAddress memoized:', actionCard?.macAddress);
        return actionCard?.macAddress;
    }, [actionCard?.macAddress]);
    const heartbeatPresence = useActuatorPresence(actionMacAddress, actType);

    useEffect(() => {
        if (!actionMacAddress) return undefined;
        return pauseActuatorProbes(actionMacAddress);
    }, [actionMacAddress]);
    
    // Debug: Track when senseType changes
    useEffect(() => {
        console.log('🔍 senseType changed to:', senseType);
    }, [senseType]);
    
    // Debug: Track when selectedTimeRange changes  
    useEffect(() => {
        console.log('🔍 selectedTimeRange changed to:', selectedTimeRange);
    }, [selectedTimeRange]);
    
    useEffect(() => {
        if (!sensorMacAddress || !actionMacAddress) return;
    
    // 🚨 URGENT DEBUG: Track historical data fetching
    console.log('🚨 ActionWrapper: Fetching historical data due to dependency change:', {
        selectedTimeRange,
        sensorMacAddress,
        actionMacAddress,
        senseType,
        timestamp: new Date().toISOString()
    });
    
        const fetchDataForTimeRange = async () => {
            try {
                const [sense, act] = await Promise.all([
                    fetchHistoricalSensorData(sensorMacAddress, senseType, selectedTimeRange, "sensor"),
                    fetchHistoricalSensorData(actionMacAddress, "status", selectedTimeRange, "action")
                ]);
                setHistoricalSensorData(sense);
                setHistoricalActionData(act);
            } catch (error) {
                console.error("Error loading historical data:", error);
            }
        };
    
        fetchDataForTimeRange();
    }, [selectedTimeRange, sensorMacAddress, actionMacAddress, senseType]);

    // The sensor card is now loaded via React Query with the useCards() hook
    // and the specific card is found using the useMemo above, so we don't need a separate effect

    // Create update function that handles both tracking changes and light schedule updates
    const updateAction = async () => {
        if (!actionCard?.macAddress || !sensorCard?.macAddress) return;

        // Validate duration for irrigation actions before proceeding
        if (isIrrigationActuator(actType)) {
            const durationValue = actionSettings.duration;
            const numValue = parseInt(durationValue);
            
            // Check if duration is valid
            if (durationValue === '' || durationValue === null || durationValue === undefined || 
                isNaN(numValue) || numValue < 1 || numValue > 32) {
                
                // Reset to previous valid value (from actionCard) or default
                const previousValue = actionCard?.duration || 4;
                handleSettingChange('duration', previousValue);
                
                setActionError({
                    title: 'Invalid duration',
                    message: 'Duration must be between 1 and 32 seconds.',
                });
                setMqttStatus(null);
                setAnimationStatus('error');
                
                return; // Don't proceed with update
            }
            
            // If duration is valid, ensure it's stored as a number
            if (typeof durationValue === 'string') {
                handleSettingChange('duration', numValue);
            }
        }
    
        setIsDataLoading(true);
        setActionError(null);
        setMqttStatus(<span style={{color: "lightblue"}}>Updating...</span>);
        setAnimationStatus('connecting');
        
        try {   
            // Simulate connecting stage
            await new Promise(resolve => setTimeout(resolve, 300));
            setAnimationStatus('sending');
            
            // Calculate thresholds based on action type
            let thresholds = {
                humidity: { low: sensorCard.humidityLow, high: sensorCard.humidityHigh }, 
                temperature: { low: sensorCard.temperatureLow, high: sensorCard.temperatureHigh }, 
                water: { low: sensorCard.waterLow, high: sensorCard.waterHigh },
                water2: { low: sensorCard.water2Low, high: sensorCard.water2High },
                light: { low: sensorCard?.lightLow, high: sensorCard?.lightHigh }
            };

            // For light actions, calculate thresholds from time settings
            if (actType === 'light') {
                const lightThresholds = calculateLightThresholds(
                    actionSettings.startTime,
                    actionSettings.endTime,
                    actionSettings.timezone
                );
                thresholds.light = lightThresholds;
            }

            // Handle immediate action safeguard if applicable
            let result;
            let trackingWasReset = false;
            if (supportsImmediateTrigger(actType)) {
                // Get latest sensor data for bounds validation
                const latestSensorData = cardData[sensorCard.macAddress];
                
                // For actuators that support immediate triggers, use the safeguard utility
                const safeguardResult = await handleImmediateActionSafeguard(
                    actionCard,
                    sensorCard,
                    handleSettingChange,
                    thresholds,
                    actionSettings.tracking,
                    setMqttStatus,
                    latestSensorData,
                    actionSettings
                );
                
                // Handle the new return format from safeguard
                if (typeof safeguardResult === 'object' && safeguardResult !== null && 'result' in safeguardResult) {
                    result = safeguardResult.result;
                    trackingWasReset = !!safeguardResult.trackingReset;
                } else {
                    result = safeguardResult;
                }
            } else {
                // For actuators that don't support immediate triggers, use standard publish
                // For irrigation, include duration parameter and sensor tracking
                if (isIrrigationActuator(actType)) {
                    const sensorTrackingValue = actionSettings.sensorTracking ? 1 : 0;
                    result = await publishMQTTStatus(
                        actionCard.macAddress, 
                        sensorCard.macAddress, 
                        thresholds, 
                        actionSettings.tracking,
                        actionSettings.duration || 4, // Default to 4 seconds if not set
                        sensorTrackingValue
                    );
                } else {
                    result = await publishMQTTStatus(
                        actionCard.macAddress, 
                        sensorCard.macAddress, 
                        thresholds, 
                        actionSettings.tracking
                    );
                }
            }

            // Simulate verifying stage
            setAnimationStatus('verifying');
            await new Promise(resolve => setTimeout(resolve, 400));
            
            // Update the action card if successful
            if (result === "success") {
                setAnimationStatus('success');
                
                // Create updated card with new thresholds if it's a light action
                let updatedAction = {};
                if (actType === 'light') {
                    updatedAction = {
                        ...actionCard,
                        tracking: actionSettings.tracking,
                        timezone: actionSettings.timezone
                    }
                    let updatedCard = {
                        ...sensorCard,
                        lightLow: thresholds.light.low,
                        lightHigh: thresholds.light.high
                    }
                    //Update card.
                    updateOneCardMutation.mutate(updatedCard);
                }
                else {
                    // If tracking was reset by the safeguard, use "1h" instead of current actionSettings
                    const finalTracking = trackingWasReset ? "1h" : actionSettings.tracking;
                    updatedAction = {
                        ...actionCard,
                        tracking: finalTracking
                    }
                    
                    // For irrigation, also save the duration and sensor tracking
                    if (isIrrigationActuator(actType)) {
                        updatedAction.duration = actionSettings.duration || 4;
                        updatedAction.sensorTracking = actionSettings.sensorTracking ?? true;
                        
                        console.log('[SENSOR_TRACKING] 💾 Saving irrigation settings to database:', {
                            macAddress: actionCard.macAddress,
                            duration: updatedAction.duration,
                            sensorTracking: updatedAction.sensorTracking,
                            tracking: updatedAction.tracking,
                            fullUpdate: updatedAction
                        });
                    }
                }

                //Update action.
                console.log('[SENSOR_TRACKING] 📤 Sending action update to server:', updatedAction);
                updateOneActionMutation.mutate(updatedAction);
                
                if (trackingWasReset) {
                    setActionError(null);
                    setMqttStatus(
                        <span style={{color: "lightgreen"}}>
                            Immediate run complete — schedule set to 1 hour.
                        </span>
                    );
                } else {
                    setActionError(null);
                    setMqttStatus(<span style={{color: "lightgreen"}}>Device Updated!</span>);
                }
            } else {
                setAnimationStatus('error');
                setMqttStatus(null);

                const detail = formatActionFailureMessage(result);
                const resetNote = (trackingWasReset || actionSettings.tracking === "2s")
                    ? ' Interval was reset to 1 hour.'
                    : '';
                const isBoundsBlock = /outside bounds|bounds not set/i.test(detail);

                setActionError({
                    title: isBoundsBlock ? 'Immediate action blocked' : 'Action failed',
                    message: `${detail}${resetNote}`,
                });
                
                // For actuators in immediate mode, ensure tracking is reset to 1h on failure
                if (supportsImmediateTrigger(actType) && (actionSettings.tracking === "2s" || trackingWasReset)) {
                    console.warn(`${actType} operation failed, ensuring tracking is reset to 1h`, result);
                    handleSettingChange("tracking", "1h");
                }
            }
        } catch (error) {
            console.error('Error updating action:', error);
            setAnimationStatus('error');
            setMqttStatus(null);
            setActionError({
                title: 'Action failed',
                message: `${error?.message || 'Unexpected error'}${
                    supportsImmediateTrigger(actType) && actionSettings.tracking === "2s"
                        ? ' Interval was reset to 1 hour.'
                        : ''
                }`,
            });
            
            // For actuators in immediate mode, reset to 1h on any error
            if (supportsImmediateTrigger(actType) && actionSettings.tracking === "2s") {
                console.warn(`Action update failed for immediate ${actType}, resetting to 1h`);
                handleSettingChange("tracking", "1h");
            }
        } finally {
            // Leave error modal open until the user dismisses it.
            // Success / in-progress animations return to idle after a short beat.
            setTimeout(() => {
                setIsDataLoading(false);
                setAnimationStatus((prev) => (prev === 'error' ? 'error' : 'idle'));
            }, 800);
            // No return statement here - avoid unsafe finally returns
        }
    };

    // These state hooks were moved up to be with other state initializations

    const latestData = actionCard ? cardData[actionCard.sensor] ?? null : null;
    const currentTimestamp = latestData?.mostRecentTimestamp ?? null;

    const handleSettingChange = (settingKey, value) => {
        setActionSettings(prev => ({
            ...prev,
            [settingKey]: value
        }));
        
        // For actuators with immediate setting, update UI to show selection
        if (supportsImmediateTrigger(actType) && settingKey === "tracking" && value === "2s") {
            setMqttStatus(<span style={{color: "lightyellow"}}>Immediate selected</span>);
        }
    }

    //useMemo to correctly fill out historicalActionData:
    const processedHistoricalActionData = useMemo(() => {
        const validHistoricalActionData = historicalActionData.filter(
            entry => entry && entry.value !== null && entry.value !== undefined
        ).map(entry => ({
            ...entry,
            //value: entry.value && parseFloat(entry.value) > 0.92 ? 1 : entry.value || 0 //Smooth out status updates to ensure they hit 1.
            value: entry.value || 0
        }));
        console.log("validHistoricalActionData", validHistoricalActionData);
        if (!historicalSensorData.length) return [];
        //scenario 1: in recent memory, irrigation sensor hasn't published
        if(!validHistoricalActionData.length) {
            return historicalSensorData.map((sensorEntry) => ({
                timestamp: sensorEntry.timestamp,
                value: 0
            }));
        }

        //scenario 2: in recent memory, irrigation sensor has published
        //Create a map of sensor timestamps for quick matching lookup:
        const sensorTimestamps = new Set(historicalSensorData.map((sensorEntry) => sensorEntry.timestamp));
        const processedData = validHistoricalActionData.map((actionEntry) => {
            //If action timestamp exists in sensor timestamps, keep it.
            if (sensorTimestamps.has(actionEntry.timestamp)) {
                return actionEntry;
            }

            //If no matching sensor timestamp, find closest sensor timestamp:
            const closestSensorTimestamp = historicalSensorData.reduce((prev, curr) => {
                return Math.abs(new Date(curr.timestamp) - new Date(actionEntry.timestamp)) < 
                Math.abs(new Date(prev.timestamp) - new Date(actionEntry.timestamp)) 
                    ? curr 
                    : prev
            });

            return {
                timestamp: closestSensorTimestamp.timestamp,
                value: actionEntry.value
            };
        });
        // Add any missing sensor timestamps with 0 value
        const actionTimestampSet = new Set(processedData.map((entry) => entry.timestamp));
        const additionalEntries = historicalSensorData
            .filter(sensorEntry => !actionTimestampSet.has(sensorEntry.timestamp))
            .map(sensorEntry => ({
                timestamp: sensorEntry.timestamp,
                value: 0
        }));
        //processedData: correctly mapped nonzero action statuses.
        //additionalEntries: correctly mapped zero action statuses.
        //We'll now sort and merge them together to create our new historicalActionData set.
        return [...processedData, ...additionalEntries].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }, [historicalSensorData, historicalActionData, actType]);

    // Determine if we should show loading state
    const isLoading = useMemo(() => {
        // Return true if we're still loading actions and don't have any cached data
        return (isActionsLoading && !actionCard && !locationAction?.macAddress) || 
               (isActionsLoading && (!actionCard || Object.keys(actionCard).length === 0));
    }, [isActionsLoading, actionCard, locationAction]);

    // If we're loading, show the loading component
    if (isLoading) {
        return <Loading message="Loading action data..." />;
    }

    // If action card is not found after loading is complete, show error
    if (!isActionsLoading && !actionCard) {
        return (
            <div className="mqtt-chart-container">
                <button className="zone-control-btn" onClick={handleBack}>
                    <i className="ri-arrow-left-line"></i>
                    <span>Back</span>
                </button>
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <h3>Action Not Found</h3>
                    <p>The action you&apos;re looking for doesn&apos;t exist or has been removed.</p>
                    <button className="zone-control-btn" onClick={handleBack}>
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mqtt-chart-container">
            {/* Header with back button and title */}
            <div className="zone-header">
                <button className="zone-control-btn" onClick={handleBack}>
                    <i className="ri-arrow-left-line"></i>
                    <span>Back</span>
                </button>
                <div className="zone-header-main">
                    <div className="zone-title-row">
                        <h2 className="zone-title">
                            <InlineRename
                                value={actionCard?.actuatorDescription || ''}
                                onSave={(next, callbacks) => {
                                    if (!actionCard) return;
                                    updateOneActionMutation.mutate(
                                        { ...actionCard, actuatorDescription: next },
                                        callbacks
                                    );
                                }}
                            />
                        </h2>
                    </div>
                    <p className="zone-updated">Last Updated: {currentTimestamp}</p>
                    {heartbeatPresence && (
                        <p className="zone-updated">Device Status: <span style={{color: heartbeatPresence.status === "online" ? "lightgreen" : heartbeatPresence.status === "offline" ? "red" : "lightblue"}}>
                            {heartbeatPresence.status === "online"
                                ? `Online · ${formatAge(Date.now() - heartbeatPresence.lastSeenAt) || "just now"}`
                                : heartbeatPresence.status === "offline"
                                    ? (heartbeatPresence.lastSeenAt != null
                                        ? `Offline · last ${heartbeatPresence.source === "heartbeat" ? "heartbeat" : "seen"} ${formatAge(Date.now() - heartbeatPresence.lastSeenAt)}`
                                        : "Offline")
                                    : "Checking..."}
                        </span></p>
                    )}
                    {isIrrigationActuator(actType) && (
                        <WateringStatus actionMac={actionMacAddress} variant="detail" />
                    )}
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="action-tabs">
                <button 
                    className={`action-tab ${activeTab === 'controls' ? 'active' : ''}`}
                    onClick={() => setActiveTab('controls')}
                >
                    <i className="ri-settings-line"></i>
                    <span>Controls</span>
                </button>
                <button 
                    className={`action-tab ${activeTab === 'automation' ? 'active' : ''}`}
                    onClick={() => setActiveTab('automation')}
                >
                    <i className="ri-time-line"></i>
                    <span>Schedule / Automate</span>
                </button>
            </div>

            {/* Controls Tab */}
            {activeTab === 'controls' && (
                <>
                    <div className="chart-time-range-selector"> 
                        {
                            mqttTimeRangeOptions.map((option) => {
                                return (
                                    <button 
                                    key={option.value}
                                    onClick={() => setSelectedTimeRange(option.value)}
                                    className={`time-range-button ${selectedTimeRange === option.value ? 'active' : ''}`}
                                    >
                                        {option.label}
                                    </button>
                                )
                            })
                        }
                    </div>

                    <div className="chart-dropdown-selector-container">
                        {actionConfig.settings.map((setting, index) => {
                            const currentValue = actionSettings[setting.key];
                            
                            return (
                                <div key={index} className="chart-dropdown-selector">
                                    <div className="chart-dropdown-selector-inner">
                                        <label className="chart-display-p">{setting.label}</label>
                                        {setting.type === "select" ? (
                                            <select
                                                value={currentValue}
                                                onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                                                className="time-range-dropdown"
                                            >
                                                {setting.options.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : setting.type === "time" ? (
                                            <input
                                            type="time"
                                            value={normalizeToTimeInputFormat(currentValue)}
                                            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                                            className={`${actType === 'light' ? 'light-action-time-input' : ''} time-range-dropdown`}
                                        />
                                        ) : setting.type === "number" && setting.key === "duration" ? (
                                            <div className="duration-input-container">
                                                <button 
                                                    type="button"
                                                    onClick={() => handleSettingChange(setting.key, Math.max(setting.min || 1, (currentValue || 4) - 1))}
                                                    className="duration-btn"
                                                    disabled={isDataLoading}
                                                >
                                                    <i className="ri-subtract-line"></i>
                                                </button>
                                                <input
                                                    type="number"
                                                    value={currentValue === '' ? '' : (currentValue || 4)}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Allow any input while typing (including empty)
                                                        handleSettingChange(setting.key, value);
                                                    }}
                                                    onBlur={(e) => {
                                                        // On blur, ensure we have a valid value
                                                        const value = e.target.value;
                                                        if (value === '' || isNaN(parseInt(value))) {
                                                            handleSettingChange(setting.key, setting.min || 1);
                                                        }
                                                    }}
                                                    min={setting.min || 1}
                                                    max={setting.max || 32}
                                                    className="duration-input"
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => handleSettingChange(setting.key, Math.min(setting.max || 32, (currentValue || 4) + 1))}
                                                    className="duration-btn"
                                                    disabled={isDataLoading}
                                                >
                                                    <i className="ri-add-line"></i>
                                                </button>
                                            </div>
                                        ) : setting.type === "number" ? (
                                            <input
                                                type="number"
                                                value={currentValue || ""}
                                                onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                                                min={setting.min}
                                                max={setting.max}
                                                className="time-range-dropdown"
                                            />
                                        ) : setting.type === "checkbox" ? (
                                            <div className="checkbox-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={currentValue ?? true}
                                                    onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                                                    id={`checkbox-${setting.key}`}
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                />
                                                {setting.description && (
                                                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                                                        <i 
                                                            className="ri-information-line"
                                                            style={{ 
                                                                fontSize: '1.1rem', 
                                                                color: showTooltip === setting.key ? '#4a9eff' : '#888', 
                                                                cursor: 'help',
                                                                transition: 'color 0.2s ease'
                                                            }}
                                                            onMouseEnter={() => setShowTooltip(setting.key)}
                                                            onMouseLeave={() => setShowTooltip(null)}
                                                        />
                                                        {showTooltip === setting.key && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                bottom: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                marginBottom: '8px',
                                                                padding: '12px 14px',
                                                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                                                color: '#fff',
                                                                fontSize: '0.875rem',
                                                                borderRadius: '6px',
                                                                width: '240px',
                                                                maxWidth: 'calc(100vw - 40px)',
                                                                whiteSpace: 'normal',
                                                                zIndex: 1000,
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                                lineHeight: '1.5',
                                                                textAlign: 'center'
                                                            }}>
                                                                {setting.description}
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '6px solid transparent',
                                                                    borderRight: '6px solid transparent',
                                                                    borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                                                }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>

                                </div>
                            );
                        })}
                    </div>

                    <div className="chart-submit-container">
                        <button
                            onClick={updateAction}
                            className="chart-submit-button"
                            disabled={isDataLoading}
                            title={isDataLoading ? 'Processing action update...' : 'Apply changes to action'}
                        >
                            {isDataLoading ? (
                                mqttStatus
                            ) : (
                                <>
                                    <i className="ri-check-line"></i>
                                    <span>Apply Changes</span>
                                </>
                            )}
                        </button>
                        {!isDataLoading && mqttStatus && !actionError && (
                            <div className="chart-status-banner" role="status">
                                <span className="chart-status-message">{mqttStatus}</span>
                                <button
                                    type="button"
                                    className="chart-status-dismiss"
                                    onClick={() => {
                                        setMqttStatus(null);
                                        setAnimationStatus('idle');
                                    }}
                                    title="Dismiss"
                                    aria-label="Dismiss status"
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                        )}
                    </div>

                    <ActionErrorModal
                        isOpen={!!actionError}
                        title={actionError?.title}
                        message={actionError?.message}
                        onClose={() => {
                            setActionError(null);
                            setAnimationStatus('idle');
                        }}
                    />

                    {/* Sensor Status Animation */}
                    <SensorStatusAnimation
                        status={animationStatus}
                        sensorName={sensorCard?.sensorDescription || 'Sensor'}
                        actuatorName={actionCard?.actuatorDescription || 'Actuator'}
                        actuatorType={actType}
                    />

                    {/* Chart Toggle Button */}
                    <div className="chart-overlay-toggle-container">
                        <button
                            onClick={() => setIsChartOverlay(!isChartOverlay)}
                            className={`chart-overlay-toggle-btn ${isChartOverlay ? 'active' : ''}`}
                            title={isChartOverlay ? 'Switch to separate charts' : 'Overlay charts for comparison'}
                        >
                            <i className={`ri-${isChartOverlay ? 'layout-column' : 'layout-2'}-line`}></i>
                            <span>{isChartOverlay ? 'Separate Charts' : 'Overlay Charts'}</span>
                        </button>
                    </div>

                    {/* Conditional Chart Rendering */}
                    {isChartOverlay ? (
                        <OverlayChart 
                            sensorType={senseType.charAt(0).toUpperCase() + senseType.slice(1)}
                            actionType={actType.charAt(0).toUpperCase() + actType.slice(1)}
                            sensorHistoricalData={historicalSensorData}
                            actionHistoricalData={processedHistoricalActionData}
                            sensorThresholds={getSensorThresholds(senseType, sensorCard)}
                            selectedTimeRange={selectedTimeRange}
                        />
                    ) : (
                        <>
                            <MqttChart 
                                sensorType={senseType.charAt(0).toUpperCase() + senseType.slice(1)}
                                historicalData={historicalSensorData}
                                backgroundColor="transparent"
                                borderColor="lightsalmon"
                                dataParser={parseAmbientData}
                                valueKey={senseType}
                                thresholds={getSensorThresholds(senseType, sensorCard)}
                                selectedTimeRange={selectedTimeRange}
                            />
                            <br />
                            <br />
                            <MqttChart 
                                sensorType={actType.charAt(0).toUpperCase() + actType.slice(1)}
                                historicalData={processedHistoricalActionData}
                                backgroundColor="transparent"
                                borderColor="lightgreen"
                                dataParser={parseAmbientData}
                                valueKey={actType}
                                selectedTimeRange={selectedTimeRange}
                            />
                        </>
                    )}
                </>
            )}

            {/* Automation Tab */}
            {activeTab === 'automation' && (
                <AutomationTab 
                    actionCard={actionCard}
                    sensorCard={sensorCard}
                    actType={actType}
                />
            )}
            
            {/* ChatBot Button */}
            <button 
                className="chatbot-toggle-button" 
                onClick={openChatBot}
                aria-label="Open Chat Assistant"
                title="Chat with Canopy AI Assistant"
            >
                <img src={chatIcon} alt="Chat" className="chatbot-icon" />
            </button>
            
            {/* ChatBot Modal */}
            {isChatModalOpen && (
                <ChatBot 
                    isModal={true}
                    closeChatModal={closeChatBot}
                    modalPosition="bottom-right"
                    metaData={{
                        macAddress: macAddress,
                        actionType: actType,
                        timeRange: selectedTimeRange
                    }}
                />
            )}
        </div>
    );
}